/**
 * QuickActions — 4 action buttons in a single horizontal row
 * Navigates to default league pages (or shows league picker if no default set)
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';
import { mobileModalStyle } from '../../../services/mobileStyleService';
import useDefaultLeague from '../../../hooks/useDefaultLeague';
import MobileSectionHeader from './MobileSectionHeader';

const C = dynastyTheme.tokens.colors;
const HAIRLINE = `1px solid ${C.hairline}`;

const iconGlowStyle = { position: 'relative', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const glowDiscStyle = { position: 'absolute', width: 32, height: 32, borderRadius: '50%', background: `radial-gradient(circle, ${C.primaryTint.t15} 0%, transparent 70%)` };
const svgStyle = { position: 'relative' };

const LiveScoringIcon = () => <svg viewBox="0 0 24 24" fill={C.primary} stroke={C.primary} strokeWidth="1" width="22" height="22" style={svgStyle}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>;
const FreeAgentsIcon = () => <svg viewBox="0 0 24 24" fill={C.primary} stroke={C.primary} strokeWidth="1" width="22" height="22" style={svgStyle}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
const BoxScoresIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2" width="22" height="22" style={svgStyle}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
const StandingsIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2.5" width="22" height="22" style={svgStyle}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;

const actions = [
  { id: 'live-scoring', label: 'Live Scoring', Icon: LiveScoringIcon },
  { id: 'free-agents', label: 'Free Agents', Icon: FreeAgentsIcon },
  { id: 'box-scores', label: 'Box Scores', Icon: BoxScoresIcon },
  { id: 'standings', label: 'Standings', Icon: StandingsIcon },
];

// Map action IDs to MobileLeagueHome page IDs
const actionToPage = {
  'live-scoring': 'live-scoring',
  'free-agents': 'free-agents',
  'box-scores': 'live-scoring',
  'standings': 'standings',
};

function QuickActions({ leagues }) {
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const { defaultLeagueId } = useDefaultLeague(leagues, true);

  const defaultLeague = useMemo(() => {
    if (!defaultLeagueId || !leagues) return null;
    return leagues.find(l => l.league_id === defaultLeagueId) || null;
  }, [defaultLeagueId, leagues]);

  const navigateToAction = (actionId, leagueId) => {
    if (actionId === 'box-scores') {
      navigate(`/leagues/${leagueId}/team`, { state: { targetPage: 'box-scores' } });
    } else {
      const page = actionToPage[actionId];
      navigate(`/leagues/${leagueId}`, { state: { initialPage: page } });
    }
  };

  const handleAction = (actionId) => {
    if (defaultLeagueId) {
      navigateToAction(actionId, defaultLeagueId);
    } else {
      setPendingAction(actionId);
      setShowPicker(true);
    }
  };

  const handlePickLeague = (leagueId) => {
    setShowPicker(false);
    if (pendingAction) {
      navigateToAction(pendingAction, leagueId);
      setPendingAction(null);
    }
  };

  if (!leagues || leagues.length === 0) return null;

  return (
    <>
      <div style={{ borderTop: HAIRLINE }}>
        <MobileSectionHeader
          title="Quick Actions"
          subtitle={defaultLeague ? `Default: ${defaultLeague.league_name}` : undefined}
        />

        {/* 4 FLAT squared tiles in a single row (no rounded card) */}
        <div style={{ display: 'flex', gap: 8, padding: '0 8px' }}>
          {actions.map(action => (
            <button
              key={action.id}
              onClick={() => handleAction(action.id)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '14px 4px',
                backgroundColor: C.ground.screenTop,
                border: `1px solid ${C.border}`,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div style={iconGlowStyle}>
                <div style={glowDiscStyle} />
                <action.Icon />
              </div>
              <span style={{
                fontSize: '10px',
                fontWeight: '600',
                color: C.mutedLight,
                textAlign: 'center',
                lineHeight: '1.2',
              }}>
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* League picker modal */}
      {showPicker && (
        <div
          onClick={() => { setShowPicker(false); setPendingAction(null); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: mobileModalStyle.backdrop,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '340px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[800],
              borderRadius: '12px',
              padding: '20px',
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            }}
          >
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: dynastyTheme.tokens.colors.neutral[50],
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              Choose a League
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {leagues.map(league => (
                <button
                  key={league.league_id}
                  onClick={() => handlePickLeague(league.league_id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    backgroundColor: dynastyTheme.tokens.colors.neutral[900],
                    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: dynastyTheme.tokens.colors.primary + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: dynastyTheme.tokens.colors.primary,
                    flexShrink: 0,
                  }}>
                    {(league.league_name || 'L')[0].toUpperCase()}
                  </div>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: dynastyTheme.tokens.colors.neutral[50],
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {league.league_name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuickActions;
